import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
    Alert,
    Grid,
    Button,
    Typography,
    Stack,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Input, CommonModal } from '../../Common';
import Routes from '../../../routes';
import { trakrefBlue, Grey2 } from '../../../color_utils';
import { flattenRuleTree, buildRuleTree } from './rulesetUtils';
import { readImportFile } from './importExportUtils';

const EMPTY_RULESET_JSON = '{\n  "Rules": []\n}';

const initialFormValue = {
    name: '',
    type: '',
    subType: '',
};

const AddBusinessRuleModal = ({
    modalOpen,
    onClose,
    onCreated
}) => {
    const [form, setForm] = useState(initialFormValue);
    const [imported, setImported] = useState(null);
    const [saving, setSaving] = useState(false);
    const importInputRef = useRef(null);

    const onChangeForm = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const reset = () => {
        setForm(initialFormValue);
        setImported(null);
    };

    const importFile = async (file) => {
        try {
            const { meta, rulesetJson, ruleset } = await readImportFile(file);
            const ruleCount = flattenRuleTree(buildRuleTree(ruleset.groups)).length;
            setImported({ fileName: file.name, rulesetJson, ruleCount });
            if (meta) {
                setForm(prev => ({
                    name: prev.name || meta.name || '',
                    type: prev.type || meta.type || '',
                    subType: prev.subType || meta.subType || '',
                }));
            }
        } catch (error) {
            toast.error(`Could not import: ${error.message}`, { duration: 8000 });
        }
    };

    const submitForm = async () => {
        if (!form.name.trim()) {
            return toast.error('Name is required.', { duration: 6000 });
        }

        setSaving(true);
        try {
            const response = await fetch(Routes.BusinessRules.Create(), {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: form.name.trim(),
                    type: form.type.trim(),
                    subType: form.subType.trim(),
                    json: imported ? imported.rulesetJson : EMPTY_RULESET_JSON,
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                toast.success(`Business rule "${data.name}" created.`, { duration: 6000 });
                reset();
                onCreated(data);
            } else if (response.status === 400) {
                const errors = await response.json();
                const messages = Array.isArray(errors)
                    ? errors.map(e => e.exceptionMessage).filter(Boolean).join('\n')
                    : '';
                toast.error(messages || 'There was an error creating the business rule.', { duration: 8000 });
            } else {
                toast.error('There was an error creating the business rule. Please try again.', { duration: 6000 });
            }
        } catch (error) {
            toast.error('There was an error creating the business rule. Please try again.', { duration: 6000 });
        }
        setSaving(false);
    };

    return (
        <CommonModal
            open={modalOpen}
            close={() => {
                reset();
                onClose();
            }}
            header="Add Business Rule"
            width={"500px"}
        >
            <Grid container pl={6} pr={6}>
                <Grid item xs={12} lg={12} mb={2} pt={2}>
                    <Stack spacing={"15px"}>
                        <Input
                            fullWidth
                            required
                            variant="outlined"
                            label="Name"
                            value={form.name}
                            onChange={e => onChangeForm("name", e.target.value)}
                        />
                        <Input
                            fullWidth
                            variant="outlined"
                            label="Type"
                            helperText="e.g. AssetTag"
                            alwaysShowHelper
                            value={form.type}
                            onChange={e => onChangeForm("type", e.target.value)}
                        />
                        <Input
                            fullWidth
                            variant="outlined"
                            label="Sub Type"
                            value={form.subType}
                            onChange={e => onChangeForm("subType", e.target.value)}
                        />
                        <input
                            ref={importInputRef}
                            type="file"
                            accept=".json,application/json"
                            style={{ display: 'none' }}
                            onChange={e => {
                                const file = e.target.files && e.target.files[0];
                                e.target.value = '';
                                if (file) importFile(file);
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<FileUploadIcon />}
                            onClick={() => importInputRef.current && importInputRef.current.click()}
                        >
                            Import ruleset from file (optional)
                        </Button>
                        {imported ? (
                            <Alert severity="success" sx={{ fontSize: '12px' }}>
                                Loaded {imported.ruleCount} rule{imported.ruleCount === 1 ? '' : 's'} from {imported.fileName}.
                            </Alert>
                        ) : (
                            <Typography sx={{ color: Grey2, fontSize: '12px' }}>
                                Without a file, the rule starts with an empty ruleset — add rules on
                                the detail screen after creating it.
                            </Typography>
                        )}
                        <Button
                            fullWidth
                            size='large'
                            variant='contained'
                            disabled={saving}
                            onClick={submitForm}
                            style={{
                                backgroundColor: trakrefBlue,
                                marginTop: "25px",
                                marginBottom: "15px"
                            }}
                        >
                            <Typography sx={{ padding: '1% 0%', color: '#fff', fontSize: 17, fontWeight: 400 }}>
                                {saving ? 'CREATING...' : 'CREATE BUSINESS RULE'}
                            </Typography>
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </CommonModal>
    );
};

export default AddBusinessRuleModal;
